
import React, { useEffect } from "react";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { SupportedLanguage } from "@/hooks/useTranslation";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "react-i18next";

interface LanguageSelectorProps {
  selectedLanguage?: string;
  onLanguageChange?: (language: string) => void;
  disabled?: boolean;
  size?: "default" | "sm";
  // Support both naming conventions
  value?: string;
  onChange?: (value: string) => void;
  excludeLanguage?: string;
  // Translation mode - 'document' or 'interface' 
  mode?: 'document' | 'interface';
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  selectedLanguage: propSelectedLanguage,
  onLanguageChange: propOnLanguageChange,
  value,
  onChange,
  excludeLanguage,
  disabled = false,
  size = "default",
  mode = 'document'
}) => {
  const { t, i18n } = useTranslation();
  // Use context or props for language control
  const languageContext = useLanguage();
  
  // Support both naming conventions
  const selectedLanguage = propSelectedLanguage || value || languageContext.currentLanguage;
  const handleLanguageChange = (lang: string) => {
    // If we're in document translation mode, just use the callback
    if (mode === 'document') {
      if (propOnLanguageChange) {
        propOnLanguageChange(lang);
      }
      if (onChange) {
        onChange(lang);
      }
      return;
    }
    
    // For interface mode, we'll force Danish for now
    const forcedLang = 'da';
    
    // Always use the context to change language to ensure app-wide updates
    languageContext.setLanguage(forcedLang);
    
    // If a custom handler was provided, call it too (support both naming conventions)
    if (propOnLanguageChange) {
      propOnLanguageChange(forcedLang);
    }
    if (onChange) {
      onChange(forcedLang);
    }
  };
  
  // Filter out excluded language if provided
  const supportedLanguages = excludeLanguage
    ? languageContext.supportedLanguages.filter(lang => lang.code !== excludeLanguage)
    : languageContext.supportedLanguages;
    
  const triggerClassName = size === "sm" ? "w-[120px] h-8 text-sm" : "w-[180px]";
  
  // Force re-render when language changes
  useEffect(() => {
    // This effect will run when the component mounts
    // No need for event listener as React will re-render when context changes
    
    // Make sure the component uses the correct language on mount
    if (selectedLanguage !== languageContext.currentLanguage && mode === 'interface') {
      handleLanguageChange(languageContext.currentLanguage);
    }
  }, [languageContext.currentLanguage, mode]);

  const placeholderText = t("common.selectLanguage");

  // Get localized language names based on current UI language
  const getLocalizedLanguageName = (languageCode: string, languageName: string): string => {
    // Danish language names when UI is in Danish
    if (i18n.language === 'da') {
      const danishNames: Record<string, string> = {
        'en': 'Engelsk',
        'de': 'Tysk',
        'fr': 'Fransk',
        'es': 'Spansk',
        'it': 'Italiensk',
        'nl': 'Hollandsk',
        'pl': 'Polsk',
        'sv': 'Svensk',
        'da': 'Dansk',
        'fi': 'Finsk',
        'cs': 'Tjekkisk',
        'ro': 'Rumænsk',
        'hu': 'Ungarsk',
        'bg': 'Bulgarsk',
        'el': 'Græsk',
        'sk': 'Slovakisk',
        'sl': 'Slovensk',
        'et': 'Estisk',
        'lv': 'Lettisk',
        'lt': 'Litauisk',
        'mt': 'Maltesisk',
        'pt': 'Portugisisk'
      };
      return danishNames[languageCode.toLowerCase()] || languageName;
    }
    return languageName;
  };

  return (
    <Select
      value={selectedLanguage}
      onValueChange={handleLanguageChange}
      disabled={disabled}
    >
      <SelectTrigger className={`bg-background ${triggerClassName}`}>
        <SelectValue placeholder={placeholderText} />
      </SelectTrigger>
      <SelectContent className="bg-background">
        {supportedLanguages.map((language) => (
          <SelectItem key={language.code} value={language.code}>
            <span className="flex items-center">
              {language.flag && <span className="mr-2">{language.flag}</span>}
              {getLocalizedLanguageName(language.code, language.name)}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
